import { useEffect, useRef, useState } from 'react'
import { api, ErrorDeApi, type CapturaDeParametrosDeIsr, type ParametrosDeIsr as Parametros } from '../servicios/api'
import { MESES } from '../periodos'
import { AyudaDeCampo } from '../ayuda/AyudaDeCampo'

/** `capturadaEnUtc` es un instante: se convierte a hora local (ver fechas.ts). */
const instante = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })

const mensaje = (e: unknown, respaldo: string) => (e instanceof ErrorDeApi ? e.message : respaldo)

type Perdida = { ejercicioDeOrigen: string; monto: string; origen: string }

type Formulario = {
  coeficiente: string
  origenDelCoeficiente: string
  ptuPagada: string
  origenDeLaPtu: string
  perdidas: Perdida[]
}

const VACIO: Formulario = { coeficiente: '', origenDelCoeficiente: '', ptuPagada: '', origenDeLaPtu: '', perdidas: [] }

const aFormulario = (p: Parametros): Formulario => ({
  coeficiente: String(p.coeficiente),
  origenDelCoeficiente: p.origenDelCoeficiente,
  ptuPagada: String(p.ptuPagada),
  origenDeLaPtu: p.origenDeLaPtu ?? '',
  perdidas: p.perdidas.map((x) => ({ ejercicioDeOrigen: String(x.ejercicioDeOrigen), monto: String(x.monto), origen: x.origen })),
})

/** Un campo vacío no se manda: el coeficiente ausente el servidor lo rechaza, no lo toma por cero. */
function aCaptura(f: Formulario, aplicaDesdeMes: number, basadaEnCapturaId: string | null): CapturaDeParametrosDeIsr {
  const captura: CapturaDeParametrosDeIsr = {
    perdidas: f.perdidas.map((x) => ({ ejercicioDeOrigen: Number(x.ejercicioDeOrigen), monto: Number(x.monto), origen: x.origen.trim() })),
    aplicaDesdeMes,
    basadaEnCapturaId,
  }
  if (f.coeficiente.trim() !== '') captura.coeficiente = Number(f.coeficiente)
  if (f.origenDelCoeficiente.trim() !== '') captura.origenDelCoeficiente = f.origenDelCoeficiente.trim()
  if (f.ptuPagada.trim() !== '') captura.ptuPagada = Number(f.ptuPagada)
  if (f.origenDeLaPtu.trim() !== '') captura.origenDeLaPtu = f.origenDeLaPtu.trim()
  return captura
}

/** El tramo que rige en el mes: el que empezó más tarde sin pasarse de él. El servidor los manda en orden. */
const rigeEn = (tramos: Parametros[], mes: number) => tramos.filter((t) => t.aplicaDesdeMes <= mes).pop()

/**
 * Lo que está en pantalla: el formulario y desde qué mes aplicará. `clave` dice de qué tramo y mes se
 * partió; si ya no es la de ahora (otro mes, o el tramo cambió), lo editado se descarta solo.
 */
type Edicion = { clave: string; formulario: Formulario; aplicaDesde: number }

/**
 * Parámetros de ISR de un cliente persona moral (RF-1208): el coeficiente de utilidad, la PTU pagada
 * y las pérdidas pendientes por ejercicio de origen, cada uno con su origen. Rigen por tramos del
 * ejercicio, porque la declaración anual del anterior los cambia a mitad del año (LISR arts. 14
 * fr. I y 76 fr. V). El panel muestra el tramo que rige en el mes del papel; guardar corrige ese
 * tramo o abre uno nuevo desde un mes posterior. Cada guardado es una versión: la anterior se
 * conserva y los papeles ya sellados no cambian.
 */
export function ParametrosDeIsr({ clienteId, ejercicio, mes, alCambiarLosParametros, alCambiarSucio }: {
  clienteId: string
  ejercicio: number
  /** El mes del papel: el panel muestra el tramo que rige en él. */
  mes: number
  /** Los parámetros vigentes cambiaron, aquí o en otra pestaña: un borrador calculado con los anteriores ya no vale. */
  alCambiarLosParametros: () => void
  /** Hay cambios sin guardar: mientras tanto el papel no se calcula ni se sella con otros que los que se ven. */
  alCambiarSucio: (sucio: boolean) => void
}) {
  const [tramos, setTramos] = useState<Parametros[]>()
  const [edicion, setEdicion] = useState<Edicion>()
  const [errorDeCarga, setErrorDeCarga] = useState('')
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)

  // Cada lectura de los tramos lleva su número: la respuesta de una anterior (otro ejercicio u otro
  // cliente) ya no es de esta pantalla, y un guardado que termina después tampoco.
  const lectura = useRef(0)

  async function cargar() {
    const n = ++lectura.current
    setTramos(undefined)
    setEdicion(undefined)
    setErrorDeCarga('')
    try {
      const leidos = await api.parametrosDeIsr(clienteId, ejercicio)
      if (n === lectura.current) setTramos(leidos)
    } catch (e) {
      if (n === lectura.current) setErrorDeCarga(mensaje(e, 'No se pudieron leer los parámetros del ejercicio.'))
    }
  }

  useEffect(() => {
    setError('')
    void cargar()
    return () => { lectura.current++ }
  }, [clienteId, ejercicio])

  const rige = tramos && rigeEn(tramos, mes)
  const clave = `${ejercicio}-${mes}-${rige?.id ?? ''}`
  const base: Edicion = { clave, formulario: rige ? aFormulario(rige) : VACIO, aplicaDesde: rige?.aplicaDesdeMes ?? 1 }
  const actual = edicion?.clave === clave ? edicion : base
  const sucio = tramos !== undefined && JSON.stringify(actual) !== JSON.stringify(base)

  useEffect(() => alCambiarSucio(sucio), [sucio])

  const editar = (cambio: (e: Edicion) => Partial<Edicion>) =>
    setEdicion((previa) => {
      const de = previa?.clave === clave ? previa : base
      return { ...de, ...cambio(de) }
    })

  const editarFormulario = (cambio: (f: Formulario) => Partial<Formulario>) =>
    editar((e) => ({ formulario: { ...e.formulario, ...cambio(e.formulario) } }))

  const cambiarPerdida = (i: number, cambio: Partial<Perdida>) =>
    editarFormulario((f) => ({ perdidas: f.perdidas.map((x, j) => (j === i ? { ...x, ...cambio } : x)) }))

  async function guardar() {
    const n = lectura.current
    // Corregir el tramo que rige se basa en su versión vigente; abrir uno nuevo, en ninguna.
    const basadaEn = rige && actual.aplicaDesde === rige.aplicaDesdeMes ? rige.id : null
    setGuardando(true)
    setError('')
    try {
      const guardados = await api.capturarParametrosDeIsr(clienteId, ejercicio, aCaptura(actual.formulario, actual.aplicaDesde, basadaEn))
      if (n !== lectura.current) return
      setTramos((t) => [...(t ?? []).filter((x) => x.aplicaDesdeMes !== guardados.aplicaDesdeMes), guardados]
        .sort((a, b) => a.aplicaDesdeMes - b.aplicaDesdeMes))
      setEdicion(undefined)
      alCambiarLosParametros()
    } catch (e) {
      if (n !== lectura.current) return
      setError(e instanceof ErrorDeApi && e.estado === 403
        ? 'Tu rol no puede capturar parámetros de ISR: pide a un contador o a un administrador del despacho que lo haga.'
        : mensaje(e, 'No se pudieron guardar los parámetros del ejercicio.'))
      // Otro colaborador u otra pestaña guardó otra versión: se recargan para verla antes de volver a capturar.
      if (e instanceof ErrorDeApi && e.codigo === 'PARAMETROS_CAMBIARON') {
        alCambiarLosParametros()
        void cargar()
      }
    } finally {
      setGuardando(false)
    }
  }

  const desde = rige?.aplicaDesdeMes ?? 1
  const mesesDeInicio = Array.from({ length: mes - desde + 1 }, (_, i) => desde + i)
  const { formulario } = actual

  return (
    <section aria-labelledby="titulo-parametros">
      <h2 id="titulo-parametros">Parámetros del ejercicio {ejercicio}</h2>

      {errorDeCarga ? (
        <p className="error" role="alert">{errorDeCarga}</p>
      ) : !tramos ? (
        <p className="sutil">Cargando parámetros…</p>
      ) : (
        <>
          <p className="nota">
            {rige
              ? `Rigen en ${MESES[mes - 1]} los que aplican desde ${MESES[rige.aplicaDesdeMes - 1]}, capturados el ${instante(rige.capturadaEnUtc)}. Guardar crea una versión nueva: la anterior se conserva y los papeles ya sellados no cambian.`
              : `Aún no capturas los parámetros que rigen en ${MESES[mes - 1]} de ${ejercicio}: sin ellos no se calcula el papel.`}
          </p>
          <p className="sutil">
            El coeficiente y las pérdidas cambian a mitad del ejercicio, cuando se presenta o vence la declaración anual del
            anterior, a los tres meses del cierre (LISR arts. 14 fr. I y 76 fr. V): por eso cada captura dice desde qué mes aplica.
            {tramos.length > 0 &&
              ` Tramos de ${ejercicio}: ${tramos.map((t) => `desde ${MESES[t.aplicaDesdeMes - 1]}, coeficiente ${t.coeficiente}`).join(' · ')}.`}
          </p>

          <form
            aria-label="Parámetros del ejercicio"
            onSubmit={(e) => {
              e.preventDefault()
              void guardar()
            }}
          >
            <div className="filtros">
              <label>
                Aplica desde
                <select value={actual.aplicaDesde} onChange={(e) => editar(() => ({ aplicaDesde: Number(e.target.value) }))}>
                  {mesesDeInicio.map((m) => (
                    <option key={m} value={m}>
                      {MESES[m - 1]}{m === rige?.aplicaDesdeMes ? ' · corrige el tramo que rige' : ' · tramo nuevo'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="filtros">
              <div className="con-ayuda">
                <label>
                  Coeficiente de utilidad
                  <input type="number" min="0" step="0.0001" value={formulario.coeficiente} placeholder="0.0000"
                    onChange={(e) => editarFormulario(() => ({ coeficiente: e.target.value }))} />
                </label>
                <AyudaDeCampo clave="coeficiente-de-utilidad" />
              </div>
              <label>
                De dónde sale el coeficiente
                <input value={formulario.origenDelCoeficiente} maxLength={300} placeholder="Último ejercicio con declaración presentada o vencida"
                  onChange={(e) => editarFormulario(() => ({ origenDelCoeficiente: e.target.value }))} />
              </label>
            </div>
            <div className="filtros">
              <div className="con-ayuda">
                <label>
                  PTU pagada en el ejercicio
                  <input type="number" min="0" step="0.01" value={formulario.ptuPagada} placeholder="0.00"
                    onChange={(e) => editarFormulario(() => ({ ptuPagada: e.target.value }))} />
                </label>
                <AyudaDeCampo clave="ptu-pagada" />
              </div>
              <label>
                De dónde sale la PTU
                <input value={formulario.origenDeLaPtu} maxLength={300} placeholder={`Pagada el 30/05/${ejercicio}`}
                  onChange={(e) => editarFormulario(() => ({ origenDeLaPtu: e.target.value }))} />
              </label>
            </div>

            <div className="con-ayuda">
              <h3>Pérdidas fiscales pendientes de aplicar</h3>
              <AyudaDeCampo clave="perdidas-fiscales" />
            </div>
            <p className="sutil">
              Una por ejercicio de origen, con lo que queda pendiente. Se disminuyen de los diez ejercicios siguientes (LISR art. 57);
              ContaQuick todavía no las actualiza con el INPC.
            </p>
            {formulario.perdidas.length === 0 && <p className="vacio">Sin pérdidas pendientes.</p>}
            {formulario.perdidas.map((x, i) => (
              <div className="filtros" role="group" aria-label={`Pérdida ${i + 1}`} key={i}>
                {/* Requeridos: un campo vacío llegaría como 0 (Number('') es 0) y no como un error de captura. */}
                <label>
                  Ejercicio de origen
                  <input type="number" min="2000" step="1" required value={x.ejercicioDeOrigen} placeholder={String(ejercicio - 1)}
                    onChange={(e) => cambiarPerdida(i, { ejercicioDeOrigen: e.target.value })} />
                </label>
                <label>
                  Pendiente de aplicar
                  <input type="number" min="0" step="0.01" required value={x.monto} placeholder="0.00"
                    onChange={(e) => cambiarPerdida(i, { monto: e.target.value })} />
                </label>
                <label>
                  De dónde sale
                  <input required value={x.origen} maxLength={300} placeholder={`Declaración anual ${ejercicio - 1}`}
                    onChange={(e) => cambiarPerdida(i, { origen: e.target.value })} />
                </label>
                <button type="button" className="enlace"
                  onClick={() => editarFormulario((f) => ({ perdidas: f.perdidas.filter((_, j) => j !== i) }))}>
                  Quitar
                </button>
              </div>
            ))}
            <p className="filtros">
              <button type="button" className="secundario"
                onClick={() => editarFormulario((f) => ({ perdidas: [...f.perdidas, { ejercicioDeOrigen: '', monto: '', origen: '' }] }))}>
                Agregar pérdida
              </button>
              {sucio && (
                <button type="button" className="secundario" onClick={() => { setEdicion(undefined); setError('') }}>
                  Descartar cambios
                </button>
              )}
              <button type="submit" className="primario" disabled={guardando}>
                {guardando ? 'Guardando…' : 'Guardar parámetros del ejercicio'}
              </button>
            </p>
          </form>

          {error && <p className="error" role="alert">{error}</p>}
        </>
      )}
    </section>
  )
}
